import { create } from 'zustand';

export type RoundPhase =
  | 'waiting'
  | 'starting'
  | 'question'
  | 'reveal'
  | 'transitioning'
  | 'word_chain'
  | 'word_chain_reveal'
  | 'ended';

export type LiveQuestion = {
  question_id: string;
  order_index: number;
  question_text: string;
  question_type: 'quiz' | 'word_scramble';
  time_limit_sec: number;
  server_started_at: number;
  total_questions?: number;
  canonical_answer?: string;
  accepted_answers?: string[];
  answer_format?: 'text' | 'multiple_choice';
  choices?: string[];
};

// ── Word Chain types ──────────────────────────────────────────────────────────

export type WordChainData = {
  game_question_id: string;
  chain_hints: string[];
  total_words: number;
  time_limit_sec: number;
  server_started_at: number;
  question_text?: string;
};

export type WordChainRevealEntry = {
  participant_id: string;
  display_name: string | null;
  avatar_url: string | null;
  submitted_words: string[];
  correct_mask: boolean[];
  score_awarded: number;
  response_time_ms: number | null;
  rank: number;
};

export type WordChainRevealData = {
  canonical_answer: string;
  correct_words: string[];
  explanation: string | null;
  per_player_result: WordChainRevealEntry[];
};

// ── Shared types ──────────────────────────────────────────────────────────────

export type LiveScoreboardEntry = {
  rank: number;
  participant_id: string;
  display_name: string | null;
  avatar_url: string | null;
  total_score: number;
  correct_count?: number;
  total_response_time_ms?: number;
};

export type PerPlayerResult = {
  participant_id: string;
  display_name: string | null;
  avatar_url: string | null;
  is_correct: boolean;
  score_awarded: number;
  response_time_ms: number | null;
  answer_text: string | null;
  new_total_score: number;
  rank_after: number;
};

export type MyLastResult = {
  is_correct: boolean;
  score_awarded: number;
  new_total_score: number;
  canonical_answer: string;
  rank_after?: number;
};

export type JoinedPayload = {
  round_id: string;
  phase: RoundPhase;
  participants: LiveScoreboardEntry[];
  is_spectator: boolean;
  my_participant_id?: string;
  current_question?: LiveQuestion | null;
  round?: {
    id: string;
    name: string;
    status: string;
    number_of_questions: number;
    time_limit_per_question_sec: number;
  };
  participant?: { id: string; role_in_round: string; total_score: number };
};

export type QuestionStartedPayload = LiveQuestion;

export type QuestionRevealedPayload = {
  question_id: string;
  canonical_answer: string;
  explanation: string | null;
  reveal_ends_at: number;
  per_player_result: PerPlayerResult[];
  leaderboard_top_n: LiveScoreboardEntry[];
};

export type RoundEndedPayload = {
  round_id: string;
  ended_at: string;
  final_leaderboard: LiveScoreboardEntry[];
};

export type AnswerCountPayload = {
  question_id: string;
  answered_count: number;
  total_players: number;
};

export type ParticipantJoinedPayload = {
  participant_id: string;
  display_name: string | null;
  avatar_url: string | null;
  role_in_round: 'player' | 'spectator';
};

export type ParticipantLeftPayload = {
  participant_id: string;
  employee_id?: string;
};

export type GameNotification = {
  id: string;
  type: 'join' | 'leave' | 'info' | 'countdown';
  message: string;
  timestamp: number;
};

export type ChatMessage = {
  id: string;
  sender_id: string;
  sender_name: string | null;
  sender_avatar: string | null;
  is_host: boolean;
  message: string;
  sent_at: number;
};

// ── State shape ───────────────────────────────────────────────────────────────

type GameState = {
  roundId: string | null;
  roundName: string | null;
  totalQuestions: number;
  phase: RoundPhase;
  participants: LiveScoreboardEntry[];
  prevParticipants: LiveScoreboardEntry[];
  perPlayerResult: PerPlayerResult[];
  finalLeaderboard: LiveScoreboardEntry[];
  currentQuestion: LiveQuestion | null;
  myAnswerSubmitted: boolean;
  myLastResult: MyLastResult | null;
  isSpectator: boolean;
  myParticipantId: string | null;
  connectionStatus: 'connected' | 'reconnecting' | 'disconnected';
  answerCount: AnswerCountPayload | null;
  notifications: GameNotification[];
  chatMessages: ChatMessage[];
  startingAt: number | null;
  revealEndsAt: number | null;
  currentExplanation: string | null;
  currentCanonicalAnswer: string | null;
  nextQuestionStartsAt: number | null;
  nextQuestionOrderIndex: number | null;

  // ── Word Chain ──
  wordChainData: WordChainData | null;
  wordChainSubmitCount: { submitted_count: number; total_players: number } | null;
  wordChainRevealData: WordChainRevealData | null;
  /** Per-word results received from server for the current word_chain game */
  wordChainWordResults: { word_index: number; correct: boolean; score_delta: number }[];
  /** Participants who answered each word correctly, keyed by word_index, ordered by submit time */
  wordChainCorrectByWord: Record<number, Array<{ participant_id: string; display_name: string | null; avatar_url: string | null }>>;
  /** Index of the word currently being played (-1 = transitioning between phases) */
  wordChainCurrentWordIndex: number;
  /** When the current word phase started (ms epoch) */
  wordChainWordPhaseStartedAt: number | null;
  /** Time limit for the current word phase */
  wordChainWordPhaseTimeLimitSec: number;
  /** Leaderboard shown after each word phase ends (null = no phase ended yet) */
  wordChainPhaseLeaderboard: Array<{ rank: number; participant_id: string; display_name: string | null; avatar_url: string | null; total_score: number }> | null;

  // ── Actions ──
  setRoundJoined: (data: JoinedPayload) => void;
  setQuestionStarted: (data: QuestionStartedPayload) => void;
  setAnswerSubmitted: () => void;
  setQuestionRevealed: (data: QuestionRevealedPayload, myParticipantId: string | null) => void;
  setScoreboard: (entries: LiveScoreboardEntry[]) => void;
  setRoundEnded: (data: RoundEndedPayload) => void;
  setConnectionStatus: (s: GameState['connectionStatus']) => void;
  setAnswerCount: (data: AnswerCountPayload) => void;
  addParticipant: (data: ParticipantJoinedPayload) => void;
  removeParticipant: (data: ParticipantLeftPayload) => void;
  addNotification: (n: Omit<GameNotification, 'id' | 'timestamp'>) => void;
  addChatMessage: (m: Omit<ChatMessage, 'id'>) => void;
  setStartingAt: (at: number | null) => void;
  setQuestionStarting: (data: { order_index: number; total_questions: number; starts_at: number }) => void;
  setWordChainStarted: (data: WordChainData) => void;
  setWordChainSubmitCount: (data: { submitted_count: number; total_players: number }) => void;
  setWordChainRevealed: (data: WordChainRevealData) => void;
  addWordChainWordResult: (result: { word_index: number; correct: boolean; score_delta: number }) => void;
  addWordChainWordCorrect: (data: { word_index: number; participant_id: string; display_name: string | null; avatar_url: string | null }) => void;
  setWordChainWordPhaseStarted: (data: { word_index: number; starts_at: number; time_limit_sec: number }) => void;
  setWordChainWordPhaseEnded: (data: { word_index: number; leaderboard: Array<{ rank: number; participant_id: string; display_name: string | null; avatar_url: string | null; total_score: number }> }) => void;
  reset: () => void;
};

const initialState = {
  roundId: null,
  roundName: null,
  totalQuestions: 0,
  phase: 'waiting' as RoundPhase,
  participants: [] as LiveScoreboardEntry[],
  prevParticipants: [] as LiveScoreboardEntry[],
  perPlayerResult: [] as PerPlayerResult[],
  finalLeaderboard: [] as LiveScoreboardEntry[],
  currentQuestion: null,
  myAnswerSubmitted: false,
  myLastResult: null,
  isSpectator: false,
  myParticipantId: null,
  connectionStatus: 'disconnected' as const,
  answerCount: null,
  notifications: [] as GameNotification[],
  chatMessages: [] as ChatMessage[],
  startingAt: null,
  revealEndsAt: null,
  currentExplanation: null,
  currentCanonicalAnswer: null,
  nextQuestionStartsAt: null,
  nextQuestionOrderIndex: null,
  wordChainData: null,
  wordChainSubmitCount: null,
  wordChainRevealData: null,
  wordChainWordResults: [],
  wordChainCorrectByWord: {},
  wordChainCurrentWordIndex: -1,
  wordChainWordPhaseStartedAt: null,
  wordChainWordPhaseTimeLimitSec: 30,
  wordChainPhaseLeaderboard: null,
};

export const gameStore = create<GameState>((set) => ({
  ...initialState,

  setRoundJoined: (d) =>
    set((state) => ({
      roundId: d.round_id,
      roundName: d.round?.name ?? null,
      totalQuestions:
        d.round?.number_of_questions ??
        d.current_question?.total_questions ??
        state.totalQuestions,
      phase: d.phase,
      participants: d.participants,
      isSpectator: d.is_spectator,
      currentQuestion: d.current_question ?? null,
      myParticipantId: d.my_participant_id ?? d.participant?.id ?? null,
    })),

  setQuestionStarted: (d) =>
    set((state) => ({
      phase: 'question',
      currentQuestion: d,
      myAnswerSubmitted: false,
      myLastResult: null,
      answerCount: null,
      startingAt: null,
      revealEndsAt: null,
      currentExplanation: null,
      currentCanonicalAnswer: null,
      perPlayerResult: [],
      totalQuestions: d.total_questions ?? state.totalQuestions,
      nextQuestionStartsAt: null,
      nextQuestionOrderIndex: null,
    })),

  setAnswerSubmitted: () => set({ myAnswerSubmitted: true }),

  setQuestionRevealed: (d, myParticipantId) => {
    const myResult = d.per_player_result.find((r) => r.participant_id === myParticipantId) ?? null;
    set((state) => ({
      phase: 'reveal',
      revealEndsAt: d.reveal_ends_at,
      currentExplanation: d.explanation ?? null,
      currentCanonicalAnswer: d.canonical_answer,
      prevParticipants: state.participants,
      perPlayerResult: d.per_player_result,
      participants: d.leaderboard_top_n,
      myLastResult: myResult
        ? {
            is_correct: myResult.is_correct,
            score_awarded: myResult.score_awarded,
            new_total_score: myResult.new_total_score,
            canonical_answer: d.canonical_answer,
            rank_after: myResult.rank_after,
          }
        : null,
    }));
  },

  setScoreboard: (entries) => set({ participants: entries }),

  setRoundEnded: (d) =>
    set({
      phase: 'ended',
      currentQuestion: null,
      finalLeaderboard: d.final_leaderboard,
      startingAt: null,
      revealEndsAt: null,
    }),

  setConnectionStatus: (connectionStatus) => set({ connectionStatus }),

  setAnswerCount: (data) => set({ answerCount: data }),

  addParticipant: (data) =>
    set((state) => {
      if (state.participants.some((p) => p.participant_id === data.participant_id)) return state;
      const newEntry: LiveScoreboardEntry = {
        rank: state.participants.length + 1,
        participant_id: data.participant_id,
        display_name: data.display_name,
        avatar_url: data.avatar_url,
        total_score: 0,
        correct_count: 0,
      };
      return { participants: [...state.participants, newEntry] };
    }),

  removeParticipant: (data) =>
    set((state) => ({
      participants: state.participants.filter(
        (p) => p.participant_id !== data.participant_id,
      ),
    })),

  addNotification: (n) =>
    set((state) => {
      const notification: GameNotification = {
        ...n,
        id: `${Date.now()}-${Math.random()}`,
        timestamp: Date.now(),
      };
      return { notifications: [...state.notifications, notification].slice(-50) };
    }),

  addChatMessage: (m) =>
    set((state) => ({
      chatMessages: [
        ...state.chatMessages,
        { ...m, id: `${Date.now()}-${Math.random()}` },
      ].slice(-200),
    })),

  setStartingAt: (startingAt) => set({ startingAt, phase: startingAt ? 'starting' : 'waiting' }),

  setQuestionStarting: (data) =>
    set({
      phase: 'transitioning',
      nextQuestionStartsAt: data.starts_at,
      nextQuestionOrderIndex: data.order_index,
      totalQuestions: data.total_questions,
      revealEndsAt: null,
      perPlayerResult: [],
      myLastResult: null,
    }),

  setWordChainStarted: (data) =>
    set({
      wordChainWordResults: [],
      wordChainCorrectByWord: {},
      wordChainCurrentWordIndex: -1,
      wordChainWordPhaseStartedAt: null,
      wordChainPhaseLeaderboard: null,
      phase: 'word_chain',
      wordChainData: data,
      wordChainSubmitCount: null,
      wordChainRevealData: null,
    }),

  setWordChainSubmitCount: (data) => set({ wordChainSubmitCount: data }),

  setWordChainRevealed: (data) =>
    set({
      phase: 'word_chain_reveal',
      wordChainRevealData: data,
      myAnswerSubmitted: true,
    }),

  addWordChainWordResult: (result) =>
    set((s) => ({
      wordChainWordResults: [...s.wordChainWordResults, result],
    })),

  addWordChainWordCorrect: (data) =>
    set((s) => {
      const existing = s.wordChainCorrectByWord[data.word_index] ?? [];
      // Deduplicate: ignore if this participant already in list
      if (existing.some((p) => p.participant_id === data.participant_id)) return s;
      return {
        wordChainCorrectByWord: {
          ...s.wordChainCorrectByWord,
          [data.word_index]: [...existing, { participant_id: data.participant_id, display_name: data.display_name, avatar_url: data.avatar_url }],
        },
      };
    }),

  setWordChainWordPhaseStarted: (data) =>
    set({
      wordChainCurrentWordIndex: data.word_index,
      wordChainWordPhaseStartedAt: data.starts_at,
      wordChainWordPhaseTimeLimitSec: data.time_limit_sec,
      wordChainPhaseLeaderboard: null, // clear previous leaderboard when new phase starts
    }),

  setWordChainWordPhaseEnded: (data) =>
    set({
      wordChainCurrentWordIndex: -1, // transitioning
      wordChainPhaseLeaderboard: data.leaderboard,
    }),

  reset: () => set(initialState),
}));
